import { IEpticaExctractEvent } from '@commons/history/history-mail/models/eptica-extract';
import { IAppointment } from '@commons/models/appointment';
import { ContactEntryNotHandledReason, ContactEntryVisibilityType, IContactEntry } from '@commons/models/contact-entry';
import { copy } from 'angular';
import moment from 'moment';

import { Memo } from '@contact/upgrade';

export type TimelineType = 'IN_PROCESS' | 'HISTO' | 'TREATMENT';

export interface ITimelineEntry {
  id: string;
  canalName: string;
  canalDirection: string;
  entryDate: Date;
  beginDate: Date;
  endDate: Date;
  description: string;
  contactEntry?: IContactEntry;
  hasVisibility: boolean;
  selected: boolean;
  timelineType?: TimelineType;
  mailComment?: IEpticaExctractEvent;
  responsibleId?: number;
  memo?: Memo;
  visibilityType: ContactEntryVisibilityType | string;
  appointment?: IAppointment;
  isEpticaSynchronized?: boolean;
  chatConversationUuid: string;
  isNotHandledPhone: boolean;
  extraData: any;
}

export interface ITimelineInprocessEntry extends ITimelineEntry {
  type: string;
  orderKey: Date;
  entryDateStr: string;
  beginHourStr: string;
  endHourStr: string;
}

export interface ITimelineHistoryEntry extends ITimelineEntry {
  type: string;
  date: Date;
  begin: Date;
  csrLogin: string;
  campaignType: string;
}

export interface ITimelineTreatmentEntry extends ITimelineEntry {
  type: string;
  csrFirstName: string;
  csrLastName: string;
}

export class TimelineEntry {
  static getLinkStorageKey(id: string): string {
    return 'TIMELINE_TO_LINK_' + id;
  }

  static fromContactEntry(
    data: IContactEntry,
    description: string = '',
    visibility: boolean = true,
    responsibleId?: number,
    memo?: Memo
  ): ITimelineEntry {
    let result: ITimelineEntry = {
      id: data.id,
      canalName: data.canalName,
      canalDirection: data.canalDirection,
      entryDate: data.entryDate,
      beginDate: data.beginDate,
      endDate: data.endDate,
      description: description || data.description || '',
      contactEntry: data,
      hasVisibility: visibility,
      selected: false,
      visibilityType: data.visibilityType,
      isEpticaSynchronized: data.isEpticaSynchronized,
      chatConversationUuid: data.chatConversationUuid,
      isNotHandledPhone:
        data.notHandledReason === ContactEntryNotHandledReason.ABANDONED ||
        data.notHandledReason === ContactEntryNotHandledReason.DISSUADED,
      extraData: {},
    };
    if (responsibleId) result.responsibleId = responsibleId;
    if (memo) result.memo = memo;
    return result;
  }

  static toInProcess(data: ITimelineEntry, localeSt: string, selected: boolean = false) {
    let currentChanel = data.canalName === 'EXTERNAL' ? data.canalName : data.canalName + '_' + data.canalDirection;
    let result: ITimelineInprocessEntry = copy(data) as ITimelineInprocessEntry;
    result.type = currentChanel.toLowerCase() || 'UNKNOWN';
    result.orderKey = data.beginDate || data.entryDate;
    result.entryDateStr = moment(data.entryDate).locale(localeSt).format('L') || '';
    result.beginHourStr = moment(data.beginDate).locale(localeSt).format('LT') || '';
    result.endHourStr = moment(data.endDate).locale(localeSt).format('LT') || '';
    result.timelineType = 'IN_PROCESS';
    result.selected = selected;
    return result;
  }

  static toHistory(data: ITimelineEntry) {
    let currentChanel = data.canalName === 'EXTERNAL' ? data.canalName : data.canalName + '_' + data.canalDirection;
    let result: ITimelineHistoryEntry = copy(data) as ITimelineHistoryEntry;
    result.type = currentChanel.toLowerCase() || 'UNKNOWN';
    result.date = data.entryDate;
    result.begin = result.date;
    result.csrLogin = (data.contactEntry && data.contactEntry.csrLogin) || '';
    result.campaignType = (data.contactEntry && data.contactEntry.campaignType) || '';
    result.timelineType = 'HISTO';
    return result;
  }

  static toTreatment(data: ITimelineEntry) {
    let result: ITimelineTreatmentEntry = copy(data) as ITimelineTreatmentEntry;
    result.entryDate = moment(result.entryDate).toDate();
    result.type = (data.contactEntry && data.contactEntry.interactionType) || '';
    result.csrFirstName = (data.contactEntry && data.contactEntry.csrFirstname) || '';
    result.csrLastName = (data.contactEntry && data.contactEntry.csrLastname) || '';
    result.timelineType = 'TREATMENT';
    return result;
  }
}
